import { useState } from "react";
import { useAppStore } from "../../store/app";
import { useT, type StringKey } from "../../lib/i18n/useT";
import { interactiveSignIn, silentSignIn } from "../../lib/auth/msal";
import { completeSignIn } from "../../lib/auth/session";
import logo from "../../assets/lens_logo_light_mode.svg";

function MicrosoftGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function LanguageToggle() {
  const language = useAppStore((s) => s.language);
  const setLanguage = useAppStore((s) => s.setLanguage);
  return (
    <div className="absolute top-5 right-6 flex gap-1 text-sm font-medium">
      {(["en", "nl"] as const).map((lang) => (
        <button
          key={lang}
          onClick={() => setLanguage(lang)}
          className={`rounded px-2 py-1 uppercase transition-colors ${
            language === lang
              ? "bg-slate-900 text-white"
              : "text-slate-500 hover:bg-slate-200"
          }`}
        >
          {lang}
        </button>
      ))}
    </div>
  );
}

export default function LoginScreen() {
  const t = useT();
  const authStatus = useAppStore((s) => s.authStatus);
  const user = useAppStore((s) => s.user);
  const setAuth = useAppStore((s) => s.setAuth);
  const setLastLoginTime = useAppStore((s) => s.setLastLoginTime);
  const [errorKey, setErrorKey] = useState<StringKey | null>(null);

  async function handleSignIn() {
    setErrorKey(null);
    setAuth("signing_in");
    try {
      // Silent first: retries after a repo/users.json hiccup shouldn't
      // bounce through the browser again.
      const session = (await silentSignIn()) ?? (await interactiveSignIn());
      const outcome = await completeSignIn(session, true);
      if (outcome.status === "signed_in") {
        setLastLoginTime(outcome.previousLastLogin);
        setAuth("signed_in", {
          user: session.user,
          lensUser: outcome.lensUser,
          repoPath: outcome.repoPath,
          accessToken: session.accessToken,
        });
      } else if (outcome.status === "unknown_user") {
        setAuth("unknown_user", { user: session.user });
      } else {
        // Authenticated, but no Lens folder found/picked.
        setAuth("signed_out");
        setErrorKey(
          outcome.reason === "invalid" ? "login.repo_invalid" : "login.repo_missing",
        );
      }
    } catch {
      setAuth("signed_out");
      setErrorKey("common.error");
    }
  }

  function backToSignIn() {
    // Keep the cached token: after the admin adds the oid to users.json,
    // "Try again" should resolve silently without another browser trip.
    setAuth("signed_out");
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-canvas">
      <LanguageToggle />

      <img src={logo} alt="Lens" className="mb-6 h-30 w-auto" />
      <p className="mb-12 text-base text-slate-500">{t("login.tagline")}</p>

      {authStatus === "unknown_user" ? (
        <div className="max-w-sm text-center">
          <h2 className="mb-2 text-lg font-semibold text-slate-900">
            {t("login.unknown_title")}
          </h2>
          <p className="mb-4 text-sm text-slate-600">{t("login.unknown_body")}</p>
          {user?.oid && (
            <p className="mb-6 rounded bg-slate-100 px-3 py-2 font-mono text-xs text-slate-600 select-all">
              {t("login.unknown_oid", { oid: user.oid })}
            </p>
          )}
          <button
            onClick={backToSignIn}
            className="text-sm font-medium text-slate-700 underline underline-offset-4 hover:text-slate-900"
          >
            {t("login.try_again")}
          </button>
        </div>
      ) : (
        <>
          <button
            onClick={handleSignIn}
            disabled={authStatus === "signing_in"}
            className="flex items-center gap-3 rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-default disabled:opacity-60"
          >
            {authStatus === "signing_in" ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
                {t("login.signing_in")}
              </>
            ) : (
              <>
                <MicrosoftGlyph />
                {t("login.sign_in")}
              </>
            )}
          </button>
          {errorKey && (
            <p className="mt-6 max-w-sm text-center text-sm text-red-600">
              {t(errorKey)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
